import { FC, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';

import {
  Background,
  LoadingOverlay,
  Map,
  Sidebar,
  Tabs,
  MapFilter,
  Dialog
} from '../../components';
import { Report } from '../';
import { useProjectContext } from '../../context/ProjectContext';
import { TabsType } from '../../enums';
import { navItems } from '../../routes/routes';
import { useFilterContext } from '../../context/FilterContext';
import { useMapContext } from '../../context/MapContext';
import useSaveProject from '../../api/mutations/useSaveProject';
import useProject from '../../api/queries/useProject';
import {
  getAdsApiParams,
  getMarketApiParams,
  getSoldApiParams,
} from '@/api/apiHelpers';
import { convertBoundsToSquareSides, trackUserLogin } from '@/helpers';
import useDebounce from '@/helpers/useDebounce';
import { ProjectType } from '@/types/apiTypes';
import { MapTypeIds, ROLE_TYPES } from '@/constants';
import useUser from '@/api/queries/useUser';
import { Root } from '@radix-ui/react-dialog';
import strings from '@/language/Swedish';
import { PATH } from '@/routes/Paths';

const StyledDashboard = styled.main`
  ${tw`relative flex flex-col flex-1 overflow-y-auto`}
  padding: 13px;
`;

const PageContainer = styled.div`
  ${tw`flex h-screen`};
`;

const MenuShadow = styled.i`
  ${tw`absolute w-full z-10 md:rounded-lg inset-0 bottom-1/2 sm:bottom-0 bg-gradient-to-b sm:bg-[radial-gradient(800px_200px_at_0%_0%,_var(--tw-gradient-from)_25%,_var(--tw-gradient-to)_100%),_radial-gradient(150px_100px_at_100%_0%,_var(--tw-gradient-from)_25%,_var(--tw-gradient-to)_100%)] from-slate-800/40 to-slate-800/0 pointer-events-none`};
`;

const MapContainer = styled.div`
  ${tw`relative flex-none w-full h-full backdrop-blur-xl bg-transparent`};
`;

const ButtonContainer = styled.div`
  ${tw`absolute right-16 bottom-2 flex items-end flex-row gap-2 md:items-end items-center`};
  justify-content: flex-end;
`;

const HideWrapper = styled.div<{ $isHidden?: boolean }>`
  ${({ $isHidden }) =>
    $isHidden ? tw`animate-slideOutTop` : tw`animate-slideInTop`};
`;

const ReportContainer = styled.div`${tw`pb-2`};`;

const Dashboard: FC = () => {
  const { id } = useParams();
  const { project, setNewProject, setSelectedTab, selectedTab, autoSave, columnOption, showPropertyLines, setShowPropertyLines } = useProjectContext();
  const debouncedProject = useDebounce(project, 1500) as ProjectType;
  const { currentCoordinates, setCoordinates, setMapTypeId, marketRectangleBounds } = useMapContext();
  const [latestSavedProjectCopy, setLatestSavedProjectCopy] = useState<any>(project);
  const [userHasRoles, setUserHasRoles] = useState(true);
  const {
    soldData,
    marketData,
    adsData,
    setAdsParams,
    setMarketParams,
    setSoldParams,
    isFilterHidden,
  } = useFilterContext();
  const { isPending: projectPending, data: projectData } = useProject(id);
  const { mutate: saveMutation } = useSaveProject();
  const { data: userData } = useUser();
  const navigate = useNavigate();
  const hasTrackedLogin = useRef(false);

  // Track user login in Google Analytics when user data loads
  useEffect(() => {
    if (userData && !hasTrackedLogin.current) {
      trackUserLogin(userData.id, userData.email);
      hasTrackedLogin.current = true;
    }
  }, [userData]);

  useEffect(() => {
    if (selectedTab !== TabsType.sold) {
      setSelectedTab(TabsType.sold);
    }
  }, []);

  useEffect(() => {
    if (projectData) {
      const { coordinates, soldParams, adsParams, marketParams, project } =
        projectData;
      if (soldParams.refData)
        setSoldParams(
          soldParams.refData,
          soldParams.weightData,
          {
            ...soldParams.filterData,
            contract_date_max: new Date('null')
          }
        );

      setAdsParams(
        adsParams.refData,
        adsParams.weightData,
        {
          ...adsParams.filterData,
          //Custom params for filters when switching project
          ad_advertising_days_max: 365,
          ad_advertising_days_min: 1
        }
      );
      if (marketParams.filterData) {
        setMarketParams(marketParams.filterData);
      }
      setCoordinates(coordinates);
      setNewProject(project);
    }
  }, [projectData]);

  useEffect(() => {
    setLatestSavedProjectCopy(project);
  }, [debouncedProject.id]);

  useEffect(() => {
    if (userData && userData.roles) {
      if (userData.roles.length > 0 && userData.roles.includes(ROLE_TYPES.BASIC)) {
        setUserHasRoles(true);
      } else {
        setUserHasRoles(false);
      }
    }
  }, [userData]);

  const handleSave = async (force = false) => {
    if ((project != latestSavedProjectCopy && project.id == latestSavedProjectCopy.id) || force) {
      const marketSquareside = convertBoundsToSquareSides(marketRectangleBounds);
      saveMutation(
        {
          coordinates: currentCoordinates,
          adsParameters: getAdsApiParams(adsData),
          soldParameters: getSoldApiParams(soldData),
          marketParameters: getMarketApiParams(marketData),
          marketSquareside: marketSquareside,
          project: project,
          columnOption: columnOption
        },
        {
          onSuccess: () => {
            setLatestSavedProjectCopy(project);
          },
        }
      );
    }
  };
  useEffect(() => {
    if (autoSave) {
      handleSave();
    }
  }, [debouncedProject]);

  return (
		<PageContainer>
			<Background />
			<Sidebar save={handleSave} />
			<StyledDashboard>
				{projectPending && <LoadingOverlay />}
				<Tabs
					items={navItems}
					setSelectedTab={setSelectedTab}
					selectedTab={selectedTab}
				>
					<MapContainer>
						<MenuShadow />
						<Map isSearchHidden={isFilterHidden} />
						<HideWrapper $isHidden={isFilterHidden}>
							<ButtonContainer>
								<ReportContainer>
									<Report save={handleSave} />
								</ReportContainer>
								<MapFilter
									mapTypeIds={MapTypeIds}
									setMapTypeId={setMapTypeId}
									setShowPropertyLines={setShowPropertyLines}
									showPropertyLines={showPropertyLines}
								/>
							</ButtonContainer>
						</HideWrapper>
					</MapContainer>
				</Tabs>
			</StyledDashboard>
			{!userHasRoles && (
				<div className='absolute top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center z-50'>
					<Root open={true} onOpenChange={() => navigate(PATH.login)}>
						<Dialog
							title={`${strings.login.wrongAccess}`}
							description={`${strings.login.wrongAccessMessage}`}
						>
						</Dialog>
					</Root>
				</div>
			)}
		</PageContainer>
	);
};

export default Dashboard;
